/**
 * Custom Workspaces — user-built pages of components arranged in a resizable
 * split tree (react-resizable-panels), no lock button. This module owns the
 * data model + pure tree operations + local persistence. The rendering lives
 * in components/workspace/WorkspaceBuilder.tsx; the palette of available
 * components in lib/workspace/componentRegistry.tsx.
 *
 * A page is a recursive split tree. A LEAF holds one component (or nothing yet)
 * and an optional `alias` (a user-typed pane title; absent ⇒ the component's
 * registry title). A SPLIT arranges its children horizontally or vertically.
 * "Add" = split a leaf; splitting a leaf whose parent already splits the same
 * direction just appends a sibling (so rows/columns stay flat and evenly sized).
 *
 * Local-only (localStorage) — there is no per-user cloud store.
 */
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import { gatedSet } from "@/lib/workspace/saveGate";
import { migrateChartLeavesToAdvanced } from "@/lib/workspace/migrateChartToAdvanced";
import { migrateOutlookEarningsWeek } from "@/lib/workspace/migrateOutlookEarningsWeek";
import { migrateOutlookMarketHeatmap } from "@/lib/workspace/migrateOutlookMarketHeatmap";
import { migrateMacroRateWatch } from "@/lib/workspace/migrateMacroRateWatch";
import { migrateOptionsTradeDesk } from "@/lib/workspace/migrateOptionsTradeDesk";
import { migrateOptionsFullDesk } from "@/lib/workspace/migrateOptionsFullDesk";
import { migrateOptionsUndoRestores } from "@/lib/workspace/migrateOptionsUndoRestores";
import { migrateDailyBriefMarketBets } from "@/lib/workspace/migrateDailyBriefMarketBets";
import { migrateOverviewSpendingPace } from "@/lib/workspace/migrateOverviewSpendingPace";
import { migrateFundamentalsPictures } from "@/lib/workspace/migrateFundamentalsPictures";
import { migrateMacroAdditions } from "@/lib/workspace/migrateMacroAdditions";

export type WsLeaf = {
  id: string;
  t: "leaf";
  component: string | null;
  /** User-typed pane title. Absent ⇒ the component registry title. */
  alias?: string;
};

export type WsNode =
  | WsLeaf
  | {
      id: string;
      t: "split";
      dir: "h" | "v";
      children: WsNode[];
      /** Starting percentages for a horizontal split's panels, before any
       *  drag. Ignored unless there is one per child and they sum to 100, so an
       *  edit that adds or removes a child falls back to equal widths. */
      sizes?: number[];
    };

function leafOf(id: string, component: string | null, alias?: string): WsLeaf {
  return alias ? { id, t: "leaf", component, alias } : { id, t: "leaf", component };
}

export type WorkspacePage = {
  id: string;
  name: string;
  root: WsNode;
  updatedAt: number;
};

const LIST_KEY = "mpp:workspaces:v1";
const ACTIVE_KEY = "mpp:workspaces:active:v1";
/** One workspace's pane tree AS THIS SCREEN ARRANGES IT. Per-machine and
 *  Display-Profile-namespaced (`isPerMachineLayoutKey`), so a 49" ultrawide can
 *  show more panes of the same workspace than a laptop does. */
export const treeKey = (pageId: string) => `mpp:ws-tree:${pageId}:v1`;

/** Browser runtime — Date.now/Math.random are fine here. */
export function wsId(prefix = "n"): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function emptyLeaf(): WsNode {
  return { id: wsId("l"), t: "leaf", component: null };
}

/**
 * Why the pool came back empty.
 *
 * `loadPages()` returns `[]` for two situations that mean OPPOSITE things:
 * the user really has no workspaces, or we could not read the ones they have.
 * Callers that show the user a message MUST tell those apart. On 2026-09-08 a
 * server freeze killed `GET /api/prefs`, the pool never hydrated, and every
 * custom page rendered "This page's workspace no longer exists" — the app
 * telling the user their work was DELETED when nothing had been.
 *
 * `absent`     — no key at all. Either a genuinely new install, or prefs have
 *                not hydrated yet / storage is unavailable. NOT proof of
 *                deletion, and NOT safe to overwrite.
 * `unreadable` — the key is there but did not parse. Corrupt or truncated.
 * `ok`         — the key parsed to an array. An empty array here IS a real
 *                empty pool.
 */
export type PagesPoolState = "ok" | "absent" | "unreadable";

export function pagesPoolState(): PagesPoolState {
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (raw == null || raw === "") return "absent";
    return Array.isArray(JSON.parse(raw)) ? "ok" : "unreadable";
  } catch {
    return "unreadable";
  }
}

export function loadPages(): WorkspacePage[] {
  // Run the Price→Advanced leaf rewrite before any consumer reads the pool /
  // tree overrides — cover paths that never call ensurePages().
  migrateChartLeavesToAdvanced();
  migrateOutlookEarningsWeek();
  migrateOutlookMarketHeatmap();
  migrateMacroRateWatch();
  migrateOptionsTradeDesk();
  migrateOptionsFullDesk();
  migrateOptionsUndoRestores();
  migrateDailyBriefMarketBets();
  migrateOverviewSpendingPace();
  migrateFundamentalsPictures();
  migrateMacroAdditions();
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as WorkspacePage[];
    return Array.isArray(arr) ? arr.filter((p) => p && typeof p.id === "string" && p.root) : [];
  } catch {
    return [];
  }
}

export function savePages(list: WorkspacePage[]): void {
  safeLocalStorageSet(LIST_KEY, JSON.stringify(list));
}

/** Persist the pool through the manual-save gate. USER edits to the split-tree
 *  (add / split / remove / assign, new / rename / delete workspace) route here
 *  so they buffer as "unsaved" until Save; initialization / migration writes use
 *  {@link savePages} directly and persist immediately. */
export function savePagesGated(list: WorkspacePage[]): void {
  gatedSet(LIST_KEY, JSON.stringify(list));
}

/**
 * The tree to RENDER for `page` on this screen: the active Display Profile's own
 * arrangement of it if that profile has one, else the shared library copy.
 *
 * This is what makes per-screen layouts possible without triplicating the
 * library. There is still ONE workspace called "Daily Brief"; each profile just
 * holds its own arrangement of it, and a profile that has never restructured it
 * renders the shared tree exactly as before. Structural edits go through
 * {@link saveTreeGated}, never back into the pool.
 */
export function effectiveRoot(page: WorkspacePage): WsNode {
  try {
    const raw = safeLocalStorageGet(treeKey(page.id));
    if (!raw) return page.root;
    const root = JSON.parse(raw) as WsNode;
    // Guard: a truncated / half-written override must not blank the page.
    if (root && (root.t === "leaf" || root.t === "split")) return root;
  } catch {
    /* unreadable override — fall back to the library copy */
  }
  return page.root;
}

/** Persist a structural edit as THIS profile's arrangement of `pageId`, through
 *  the manual-save gate (so it buffers as "Unsaved changes" like every other
 *  arrangement edit). Deliberately does NOT touch the shared pool. */
export function saveTreeGated(pageId: string, root: WsNode): void {
  gatedSet(treeKey(pageId), JSON.stringify(root));
}

/** True when the active profile has its own arrangement of this workspace —
 *  i.e. editing it here will not affect the other screens. */
export function hasOwnTree(pageId: string): boolean {
  return safeLocalStorageGet(treeKey(pageId)) !== null;
}

export function getActiveId(): string | null {
  return safeLocalStorageGet(ACTIVE_KEY);
}
export function setActiveId(id: string): void {
  safeLocalStorageSet(ACTIVE_KEY, id);
}

export function makePage(name: string): WorkspacePage {
  return { id: wsId("p"), name, root: emptyLeaf(), updatedAt: Date.now() };
}

/** Ensure at least one page exists; returns the list + the active id. */
export function ensurePages(): { list: WorkspacePage[]; activeId: string } {
  let list = loadPages();
  if (list.length === 0) {
    const p = makePage("My workspace");
    list = [p];
    savePages(list);
    setActiveId(p.id);
    return { list, activeId: p.id };
  }
  let activeId = getActiveId();
  if (!activeId || !list.some((p) => p.id === activeId)) {
    activeId = list[0]!.id;
    setActiveId(activeId);
  }
  return { list, activeId };
}

// ── Pure tree operations (return a new tree) ──

/** Assign (or clear) the component held by a leaf. A new component drops any
 *  custom title — the alias was for the previous occupant. */
export function assignComponent(node: WsNode, leafId: string, component: string | null): WsNode {
  if (node.t === "leaf") return node.id === leafId ? leafOf(node.id, component) : node;
  return { ...node, children: node.children.map((c) => assignComponent(c, leafId, component)) };
}

/** Set or clear a leaf's custom title. Empty / whitespace clears it. */
export function renameLeaf(node: WsNode, leafId: string, alias: string | null): WsNode {
  const trimmed = alias?.trim() || undefined;
  if (node.t === "leaf") return node.id === leafId ? leafOf(node.id, node.component, trimmed) : node;
  return { ...node, children: node.children.map((c) => renameLeaf(c, leafId, alias)) };
}

/** Split a leaf, adding a fresh empty sibling. If the leaf's parent already
 *  splits in `dir`, append the sibling to it (flat, even). Otherwise wrap the
 *  leaf in a new split. */
export function splitLeaf(node: WsNode, leafId: string, dir: "h" | "v"): WsNode {
  if (node.t === "leaf") {
    if (node.id === leafId) return { id: wsId("s"), t: "split", dir, children: [node, emptyLeaf()] };
    return node;
  }
  const idx = node.children.findIndex((c) => c.t === "leaf" && c.id === leafId);
  if (idx >= 0 && node.dir === dir) {
    const children = [...node.children];
    children.splice(idx + 1, 0, emptyLeaf());
    return { ...node, children };
  }
  return { ...node, children: node.children.map((c) => splitLeaf(c, leafId, dir)) };
}

/** The leaf with this id, or undefined. */
export function findLeaf(node: WsNode, leafId: string): Extract<WsNode, { t: "leaf" }> | undefined {
  if (node.t === "leaf") return node.id === leafId ? node : undefined;
  for (const child of node.children) {
    const found = findLeaf(child, leafId);
    if (found) return found;
  }
  return undefined;
}

/** Two panes trade contents; both keep their id, so the split tree's geometry is
 *  untouched — the boxes stay where they were sized and only what's inside them
 *  moves. (Per-pane SETTINGS follow the component separately, via
 *  `swapPaneStorage` in lib/workspace/paneStorage.ts.) */
export function swapLeafComponents(root: WsNode, aId: string, bId: string): WsNode {
  if (aId === bId) return root;
  const a = findLeaf(root, aId);
  const b = findLeaf(root, bId);
  if (!a || !b) return root;
  const walk = (node: WsNode): WsNode => {
    if (node.t === "leaf") {
      if (node.id === aId) return leafOf(node.id, b.component, b.alias);
      if (node.id === bId) return leafOf(node.id, a.component, a.alias);
      return node;
    }
    return { ...node, children: node.children.map(walk) };
  };
  return walk(root);
}

/** Insert `leaf` next to the target leaf, splitting in `dir`. Flat like
 *  {@link splitLeaf}: a parent that already splits that way just gains a child. */
function insertBesideLeaf(node: WsNode, targetId: string, dir: "h" | "v", before: boolean, leaf: WsNode): WsNode {
  if (node.t === "leaf") {
    if (node.id !== targetId) return node;
    return { id: wsId("s"), t: "split", dir, children: before ? [leaf, node] : [node, leaf] };
  }
  const idx = node.children.findIndex((c) => c.t === "leaf" && c.id === targetId);
  if (idx >= 0 && node.dir === dir) {
    const children = [...node.children];
    children.splice(before ? idx : idx + 1, 0, leaf);
    return { ...node, children };
  }
  return { ...node, children: node.children.map((c) => insertBesideLeaf(c, targetId, dir, before, leaf)) };
}

export type DropEdge = "left" | "right" | "top" | "bottom";

/** Drop a NEW component against one edge of an existing pane — the palette
 *  dragged straight onto the layout, rather than split-then-pick. */
export function addLeafAtEdge(
  root: WsNode,
  targetId: string,
  edge: DropEdge,
  newLeafId: string,
  component: string | null,
): WsNode {
  if (!findLeaf(root, targetId)) return root;
  const dir = edge === "left" || edge === "right" ? "h" : "v";
  const before = edge === "left" || edge === "top";
  return insertBesideLeaf(root, targetId, dir, before, leafOf(newLeafId, component));
}

/**
 * Dock a pane against one edge of another: the target splits, the dragged
 * component lands in the new slot, and the slot it came from is removed (a
 * split left with one child collapses, same as any other removal).
 *
 * `newLeafId` is passed in rather than minted here so the caller can move the
 * pane's saved state onto it in the same gesture.
 */
export function moveLeafToEdge(
  root: WsNode,
  sourceId: string,
  targetId: string,
  edge: DropEdge,
  newLeafId: string,
): WsNode {
  if (sourceId === targetId) return root;
  const source = findLeaf(root, sourceId);
  if (!source || !findLeaf(root, targetId)) return root;
  const dir = edge === "left" || edge === "right" ? "h" : "v";
  const before = edge === "left" || edge === "top";
  const landed = leafOf(newLeafId, source.component, source.alias);
  return removeLeaf(insertBesideLeaf(root, targetId, dir, before, landed), sourceId);
}

/** Remove a leaf. A split left with one child collapses into it. Returns null
 *  if the whole subtree is gone (callers fall back to a fresh empty leaf). */
function removeLeafInner(node: WsNode, leafId: string): WsNode | null {
  if (node.t === "leaf") return node.id === leafId ? null : node;
  const children = node.children
    .map((c) => removeLeafInner(c, leafId))
    .filter((c): c is WsNode => c !== null);
  if (children.length === 0) return null;
  if (children.length === 1) return children[0]!;
  return { ...node, children };
}

export function removeLeaf(root: WsNode, leafId: string): WsNode {
  return removeLeafInner(root, leafId) ?? emptyLeaf();
}

/** Workspaces a pane can be sent to — the pool minus the one you're on. */
export function pageMoveTargets(currentPageId: string | null): Array<{ id: string; name: string }> {
  return loadPages()
    .filter((p) => p.id !== currentPageId)
    .map((p) => ({ id: p.id, name: p.name }));
}

/**
 * Append a component to ANOTHER workspace — the receiving half of "move this
 * pane to another page". Lands as a new column on the target's top-level row
 * (or fills it, if the target is still one empty pane).
 *
 * Writes wherever that page's arrangement actually lives: this profile's own
 * tree if it has diverged, otherwise the shared library copy — the same rule
 * {@link effectiveRoot} reads by, so the pane shows up where the user will next
 * open that page. Both writes are Save-gated like any other arrangement edit.
 */
export function appendComponentToPage(
  pageId: string,
  component: string,
  newLeafId: string,
  alias?: string,
): boolean {
  const pool = loadPages();
  const page = pool.find((p) => p.id === pageId);
  if (!page) return false;
  const leaf: WsNode = leafOf(newLeafId, component, alias);
  const root = effectiveRoot(page);
  const next: WsNode =
    root.t === "leaf" && root.component === null
      ? leaf // a lone empty pane takes the component instead of gaining a sibling
      : root.t === "split" && root.dir === "h"
        ? { ...root, children: [...root.children, leaf] }
        : { id: wsId("s"), t: "split", dir: "h", children: [root, leaf] };
  if (hasOwnTree(pageId)) saveTreeGated(pageId, next);
  else savePagesGated(pool.map((p) => (p.id === pageId ? { ...p, root: next, updatedAt: Date.now() } : p)));
  return true;
}

export function countLeaves(node: WsNode): number {
  return node.t === "leaf" ? 1 : node.children.reduce((s, c) => s + countLeaves(c), 0);
}

export function stackedTree(components: Array<string | null>, dir: "h" | "v"): WsNode {
  const leaves: WsNode[] = (components.length ? components : [null]).map((c) => ({
    id: wsId("l"),
    t: "leaf",
    component: c,
  }));
  if (leaves.length === 1) return leaves[0]!;
  return { id: wsId("s"), t: "split", dir, children: leaves };
}

/** One place in a seeded row: a pane, or a column of panes stacked top to
 *  bottom (the Forecast's inputs beside a column of charts). */
export type SeedCell = string | readonly string[];

/** Rows of side-by-side panes, stacked top to bottom — a seed that names its
 *  own arrangement instead of the balanced grid. `sizes[i]`, when given, is
 *  row i's starting widths in percent (the split's `sizes` hint). A cell that
 *  is a list is a column of panes inside its row. */
export function rowsTree(rows: ReadonlyArray<ReadonlyArray<SeedCell>>, sizes?: ReadonlyArray<readonly number[] | undefined>): WsNode {
  const cellNode = (cell: SeedCell): WsNode | null => {
    if (typeof cell === "string") return { id: wsId("l"), t: "leaf", component: cell };
    return cell.length === 0 ? null : stackedTree([...cell], "v");
  };
  const built: WsNode[] = rows
    .map((row, i) => ({ cells: row.map(cellNode).filter((n): n is WsNode => n != null), widths: sizes?.[i] }))
    .filter(({ cells }) => cells.length > 0)
    .map(({ cells, widths }) => {
      if (cells.length === 1) return cells[0]!;
      const split: WsNode = { id: wsId("s"), t: "split", dir: "h", children: cells };
      return widths && widths.length === cells.length ? { ...split, sizes: [...widths] } : split;
    });
  if (built.length === 0) return { id: wsId("l"), t: "leaf", component: null };
  return built.length === 1 ? built[0]! : { id: wsId("s"), t: "split", dir: "v", children: built };
}

/** Columns of stacked panes, left to right — the mirror of `rowsTree`.
 *  `sizes.widths` is each column's starting width and `sizes.heights[i]`
 *  column i's starting pane heights, all in percent (the split `sizes` hint);
 *  a list whose length does not match is ignored. */
export function columnsTree(
  columns: string[][],
  sizes?: { widths?: readonly number[]; heights?: ReadonlyArray<readonly number[] | undefined> },
): WsNode {
  const kept = columns.map((col, i) => ({ col, heights: sizes?.heights?.[i] })).filter(({ col }) => col.length > 0);
  const built: WsNode[] = kept.map(({ col, heights }) => {
    if (col.length === 1) return { id: wsId("l"), t: "leaf", component: col[0]! };
    const split = stackedTree(col, "v");
    return split.t === "split" && heights && heights.length === col.length ? { ...split, sizes: [...heights] } : split;
  });
  if (built.length === 0) return { id: wsId("l"), t: "leaf", component: null };
  if (built.length === 1) return built[0]!;
  const widths = sizes?.widths;
  const root: WsNode = { id: wsId("s"), t: "split", dir: "h", children: built };
  return widths && widths.length === built.length && kept.length === columns.length ? { ...root, sizes: [...widths] } : root;
}

/**
 * Which stacks of a tree `columnsTree` made take which starting row heights.
 * `rowPx` gives, for each column of the seed, a height in pixels for every
 * row but its last (the last takes what is left of the column). A column
 * whose heights do not number its rows less one, or are not all above zero,
 * gets none and starts at the band; so does a column of one pane, which has
 * no rows to size. Pure: the caller saves them.
 */
export function seedRowHeights(
  columns: string[][],
  rowPx: ReadonlyArray<readonly number[] | undefined> | undefined,
  root: WsNode,
): Array<{ stackId: string; heights: number[] }> {
  if (!rowPx) return [];
  // `columnsTree` leaves out an empty column, and a lone column is the root itself.
  const kept = columns.map((col, i) => ({ col, px: rowPx[i] })).filter(({ col }) => col.length > 0);
  const stacks = kept.length === 1 ? [root] : root.t === "split" && root.dir === "h" ? root.children : [];
  const out: Array<{ stackId: string; heights: number[] }> = [];
  kept.forEach(({ col, px }, i) => {
    const node = stacks[i];
    if (!px || !node || node.t !== "split" || node.dir !== "v") return;
    if (px.length !== col.length - 1 || !px.every((h) => Number.isFinite(h) && h > 0)) return;
    out.push({ stackId: node.id, heights: [...px] });
  });
  return out;
}

/**
 * A seed that names its whole arrangement: a pane, a row of things side by
 * side, or a column of things stacked, to any depth. `rowsTree` and
 * `columnsTree` say two levels; a page whose first column is itself two rows
 * of panes (the Scanner's funnel over its screener) needs a third.
 * `widths` are a row's starting widths in percent. `rowPx` are a column's
 * starting heights in pixels for every row but its last, which takes what is
 * left (a column's rows are pixel rows; see `seedRowHeights`).
 */
export type SeedTree =
  | string
  | { row: ReadonlyArray<SeedTree>; widths?: readonly number[] }
  | { col: ReadonlyArray<SeedTree>; rowPx?: readonly number[] };

/**
 * The tree a `SeedTree` describes, and the starting row heights its columns
 * ask for, by the id of the stack each is for. A row or a column of one
 * thing is that thing. Widths that do not number the row's children, and
 * heights that do not number a column's rows less one or are not all above
 * zero, are left out: the row splits evenly and the column starts at the
 * band. Pure: the caller saves the heights.
 */
export function seedTreeRoot(tree: SeedTree): { root: WsNode; rowHeights: Array<{ stackId: string; heights: number[] }> } {
  const rowHeights: Array<{ stackId: string; heights: number[] }> = [];
  const build = (node: SeedTree): WsNode => {
    if (typeof node === "string") return { id: wsId("l"), t: "leaf", component: node };
    if ("row" in node) {
      const children = node.row.map(build);
      if (children.length === 0) return { id: wsId("l"), t: "leaf", component: null };
      if (children.length === 1) return children[0]!;
      const split: WsNode = { id: wsId("s"), t: "split", dir: "h", children };
      return node.widths && node.widths.length === children.length ? { ...split, sizes: [...node.widths] } : split;
    }
    const children = node.col.map(build);
    if (children.length === 0) return { id: wsId("l"), t: "leaf", component: null };
    if (children.length === 1) return children[0]!;
    const split: WsNode = { id: wsId("s"), t: "split", dir: "v", children };
    const px = node.rowPx;
    if (px && px.length === children.length - 1 && px.every((h) => Number.isFinite(h) && h > 0)) rowHeights.push({ stackId: split.id, heights: [...px] });
    return split;
  };
  return { root: build(tree), rowHeights };
}

/** Build a balanced grid tree (rows of columns) holding the given components in
 *  order — the default layout for a migrated fixed workspace. `cols = ceil(√n)`.
 *  An empty list yields a single empty leaf. */
export function gridTree(components: Array<string | null>): WsNode {
  const ids = components.length ? components : [null];
  const n = ids.length;
  if (n === 1) return { id: wsId("l"), t: "leaf", component: ids[0]! };
  const cols = Math.ceil(Math.sqrt(n));
  const rows: WsNode[] = [];
  for (let i = 0; i < n; i += cols) {
    const rowLeaves: WsNode[] = ids.slice(i, i + cols).map((c) => ({ id: wsId("l"), t: "leaf", component: c }));
    rows.push(rowLeaves.length === 1 ? rowLeaves[0]! : { id: wsId("s"), t: "split", dir: "h", children: rowLeaves });
  }
  return rows.length === 1 ? rows[0]! : { id: wsId("s"), t: "split", dir: "v", children: rows };
}

/** Every leaf id currently in the tree (used to reap orphaned per-pane state). */
export function leafIds(node: WsNode): string[] {
  return node.t === "leaf" ? [node.id] : node.children.flatMap(leafIds);
}

/** Every non-empty component id used anywhere in the tree — lets the builder
 *  decide which data providers to wrap the page in. */
export function treeComponents(node: WsNode): string[] {
  if (node.t === "leaf") return node.component ? [node.component] : [];
  return node.children.flatMap(treeComponents);
}
